<template>
<div>
  <el-row type="flex" justify="space-between" class="title-bar">
    <span v-if="this.selectedNode.type === 'obj' && selectedAnalysisChartDate === null">
      차트에서 분석을 원하는 Peakload 구간을 선택해 주세요
    </span>
    <span v-else>
      조회 범위 : <em>{{startSearchDate}}</em>   ~   <em>{{endSearchDate}}</em>
    </span>
    <w-header-search-button @click="showSearchTool = !showSearchTool" />
  </el-row>
  <el-table-wrapper :data="tableData" :columns="columns" :column-default="columnDefault" :show-custom-header="true" :show-search-tool="showSearchTool" :show-overflow-tooltip="true" :pagination="true" :defaultSort="defaultSortCol"/>
    <template slot-scope="scope" slot="detail-slot">
      <span class="c-pointer" @click="showAjaxDetailPopup(scope.row)"><i class="far fa-search-plus" /></span>
    </template>
    <template slot-scope="scope" slot="uri-name">
      <span class="link c-pointer" @click="openDetailPopup(scope.row)">
        {{scope.row.uriText}}
      </span>
    </template>
  </el-table-wrapper>
  <w-transaction-detail-popup ref="detailPopup" popup-path="analysis" :scatter-data="scatterChartData" :tx-type="txTypeLabel" />
  <w-ajax-detail-popup ref="ajaxDetailPopup" :uri="uriText" />
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { clone, isNil } from 'lodash';
import { isEmpty } from '@/utils/basic';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { convertThousandSeparatorFormat, convertMillisecondsToSeconds } from '@/utils/number';
import { columnDefault } from '@/constants/table';
import WTransactionDetailPopup from '@/views/apm/transaction-matrix/components/popup/TransactionDetailPopup';
import ApmCountAPI from '@/api/performance/apm/count-info';
import WAjaxDetailPopup from '../popup/AjaxDetailPopup';


export default {
  name: 'WAjaxTable',
  components: { WAjaxDetailPopup, WTransactionDetailPopup },
  props: {
    activeTab: String,
    params: Object,
  },
  data() {
    return {
      columnDefault,
      startSearchDate: '-',
      endSearchDate: '-',
      showSearchTool: false,
      tableData: [],
      defaultSortCol: { prop: 'count', order: 'descending' },
      isAnalysisNode: false,
      txTypeLabel: '소요시간(ms)', // service에서 dataType의 default값은 "elapsed"이므로 label은 "소요시간(ms)"로 표현.
      scatterChartData: {},
      columns: [
        { prop: 'uriText', label: 'URI', searchable: true, sortable: true, scopedSlot: 'uri-name' },
        { prop: 'status', label: '응답 코드', width: 130, sortable: true, formatter: this.filterStatus },
        { prop: 'count', label: '호출 (건)', width: 130, sortable: true, formatter: this.convertThousandSeparatorFormat },
        { prop: 'duration', label: '지속 시간', width: 130, sortable: true, formatter: this.convertMillisecondsValue },
        { label: '상세보기', width: 80, scopedSlot: 'detail-slot' },
      ],
      uriText: null,
    };
  },
  computed: mapGetters(['selectedAnalysisChartDate', 'selectedNode']),
  watch: {
    /**
     * AJAX 탭일 경우 데이터를 로딩한다.
     * @param  {[type]} tabName [description]
     * @return {[type]}         [description]
     * @author jda
     */
    activeTab() {
      this.loadData();
    },
    /**
     * 관리대상이나 주기가 바뀔 경우 재로드한다.
     * @return {[type]} [description]
     * @author jda
     */
    params() {
      if (!isEmpty(this.params)) {
        this.startSearchDate = getDateToString(this.params.startDatetime);
        this.endSearchDate = getDateToString(this.params.endDatetime);
        
        this.loadData();
      } else {
        this.tableData = [];
      }
    },
    selectedAnalysisChartDate: {
      handler(value) {
        if (!isEmpty(value)) {
          this.startSearchDate = getDateToString(value.startDatetime);
          this.endSearchDate = getDateToString(value.endDatetime);
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * AJAX 리스트 조회
     * @return {[type]} [description]
     * @author jda
     */
    loadData() {
      if (this.activeTab !== 'ajax' || isEmpty(this.params)) {
        return;
      }
      ApmCountAPI.getEumAjaxList(this.params).then((response) => {
        if (response.data.success) {
          this.tableData = response.data.data;
        } else {
          this.tableData = [];
        }
      });
    },
    /**
     * Ajax 상세 팝업창을 띄운다.
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    showAjaxDetailPopup(record) {
      const detailParams = clone(this.params);
      Object.assign(detailParams, { uriHash: record.uri });
      this.uriText = record.uriText;
      this.$refs.ajaxDetailPopup.open(detailParams);
    },
    /**
     * 1000의 자리 숫자 , 표현
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertThousandSeparatorFormat(row, column, value) {
      return convertThousandSeparatorFormat(value);
    },
    /**
     * milliseconds -> seconds 단위 변환
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertMillisecondsValue(row, column, value) {
      return convertMillisecondsToSeconds(value);
    },
    /**
     * AJAX의 상태코드가 0인경우 - 표현
     * @author yelim
     */
    filterStatus(row, column, value) {
      if (isNil(value) || value === 0 || value === '0') {
        return EMPTY_TEXT;
      }
      return value;
    },
    openDetailPopup(row) {
		let scatterData = 
		{
            selectedObjectId: this.params.selectedObjectId,
            selectedObjectType: this.params.selectedObjectTypeText,
			selectedObjectTypeText: this.params.selectedObjectTypeText,
			
            onlyIncludeError: 'N',
            summaryType: 11,
            txType: 'elapsed',
			
			startDatetime: this.params.startDatetime,
			endDatetime: this.params.endDatetime,
		};
		  
      this.scatterChartData = Object.assign(scatterData, { targetHash: row.id });

      this.$refs.detailPopup.open();
    },
  },
};
</script>
<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/apm/components/table/AjaxTable.vue